import { ApolloClient, ApolloLink, Observable, split, createHttpLink } from '@apollo/client'
import { InMemoryCache } from '@apollo/client/cache'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { ServerError } from '@apollo/client/link/utils'
import { WebSocketLink } from '@apollo/client/link/ws'
import { getMainDefinition } from '@apollo/client/utilities'
import { createNetworkStatusNotifier } from 'react-apollo-network-status'
import { useState, useEffect } from 'react'

import generatedIntrospection from '../../generated/fragmentTypes.json'

const studentApiUri = import.meta.env.VITE_STUDENT_API_GQL_URI
const studentWsUri = import.meta.env.VITE_STUDENT_API_WS_URI

const { link: statusLink, useApolloNetworkStatus } = createNetworkStatusNotifier()

export const useStudentNetworkStatus = () => {
  const status = useApolloNetworkStatus()
  const [isMounted, setIsMounted] = useState(true)
  const [numPendingQueries, setNumPendingQueries] = useState(status.numPendingQueries)

  useEffect(() => {
    setNumPendingQueries(status.numPendingQueries)
  }, [status.numPendingQueries])

  useEffect(() => () => setIsMounted(false), [])

  return isMounted ? status : { numPendingQueries }
}

// Error link to retry requests on 401 (token expired).
// Each operation only retries once to prevent amplification loops.
// Exported for unit tests: the forced renewal on 401 is the
// retry path the storm fix depends on.
export const createAuthRetryLink = (
  getTokenSilently: (options?: { forceRefresh?: boolean }) => Promise<string | undefined>
) =>
  onError(({ networkError, operation, forward }) => {
    const serverError = networkError as ServerError
    if (networkError && serverError.statusCode === 401) {
      const context = operation.getContext()
      if (context.hasRetriedAuth) {
        return undefined
      }
      operation.setContext({ ...context, hasRetriedAuth: true })

      return new Observable((observer) => {
        let cancelled = false
        let innerSubscription: { unsubscribe: () => void } | undefined
        const refusedAuth = context.headers?.authorization

        // The server refused this token (401). Force one
        // renewal past the freshness check; the cooldown still applies.
        getTokenSilently({ forceRefresh: true })
          .then((newToken) => {
            if (cancelled) return
            // Retry only with a NEW credential. Re-sending the refused
            // token would burn a guaranteed second 401. No header write
            // here: the retried pass re-enters authLink downstream, and
            // authLink supplies the authorization header from the renewed
            // token.
            if (newToken && `Bearer ${newToken}` !== refusedAuth) {
              innerSubscription = forward(operation).subscribe(observer)
            } else {
              observer.error(networkError)
            }
          })
          .catch((err) => {
            if (cancelled) return
            observer.error(err)
          })

        // Teardown on unsubscribe: stop the forwarded retry and ignore a
        // late token result, so an unmounted caller leaks no work.
        return () => {
          cancelled = true
          innerSubscription?.unsubscribe()
        }
      })
    }
    return undefined
  })

// `getStudentClient` ahora acepta `isAuthenticated` y `getTokenSilently` sin tipos específicos de Auth0
const getStudentClientOIDC = (
  getTokenSilently: (options?: { forceRefresh?: boolean }) => Promise<string | undefined>,
  isAuthenticated: boolean = false
) => {
  // Link de autenticación condicional
  const authLink = isAuthenticated
    ? setContext(async (_, { headers }) => {
        const accessToken = await getTokenSilently()
        return {
          headers: {
            ...headers,
            authorization: accessToken ? `Bearer ${accessToken}` : ''
          }
        }
      })
    : null

  const http = createHttpLink({
    uri: studentApiUri
  })

  // WebSocket link solo si está autenticado
  const wsLink = isAuthenticated
    ? new WebSocketLink({
        uri: studentWsUri!,
        options: {
          reconnect: true,
          reconnectionAttempts: 30,
          connectionParams: async () => {
            const accessToken = await getTokenSilently()
            return accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
          },
          lazy: true
        }
      })
    : null

  // `splitLink` condicional para usar `wsLink` solo si está autenticado
  const splitLink = isAuthenticated
    ? split(
        ({ query }) => {
          const definition = getMainDefinition(query)
          return (
            definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
          )
        },
        wsLink!,
        http
      )
    : http

  const errorLink = createAuthRetryLink(getTokenSilently)

  // Configura `ApolloLink` para incluir `errorLink`, `authLink` y `statusLink` solo si está autenticado
  const link = isAuthenticated
    ? ApolloLink.from([errorLink, authLink!, statusLink, splitLink])
    : http

  const cache = new InMemoryCache({
    possibleTypes: generatedIntrospection.possibleTypes,
    typePolicies: {
      Query: {
        fields: {
          siteSettings: {
            merge(existing, incoming) {
              return { ...existing, ...incoming }
            }
          },
          academicPlanningSettings: {
            merge(existing, incoming) {
              return { ...existing, ...incoming }
            }
          }
        }
      },
      SiteSettings: {
        merge: true
      },
      AcademicPlanningSettings: {
        merge: true
      },
      PlanAlert: {
        keyFields: ['alertKey', 'itemId']
      },
      PlanGroup: {
        fields: {
          items: {
            merge(_existing, incoming: any[]) {
              return incoming
            }
          }
        }
      },
      AdditionalAuditReportType: {
        keyFields: ['auditReportType', 'careerCode']
      }
    }
  })

  return new ApolloClient({
    link,
    cache,
    defaultOptions: {
      watchQuery: { errorPolicy: 'all' }
    },
    connectToDevTools: import.meta.env.MODE !== 'production'
  })
}

export default getStudentClientOIDC
